import React, { useState } from "react";
import { BaseButton  } from "iebt-zing-storybook";
import { useSmartNavigateLegacy as useNavigate } from "components/SmartLink";
import { NoContent } from "components/NoContent";
import NoContentImg from "assets/images/defaults/no_challenges.png";
import { trans } from "sharedKernel/i18n";
import { TReducers, useSelector } from "store";
import {
  IMenuEvaluation,
  IMenuEvaluators,
  IMenuEvaluatorsSteps,
} from "components/menu/state/content/store/contracts";

import "tippy.js/dist/tippy.css";
import "tippy.js/animations/scale.css";
import "tippy.js/themes/translucent.css";
import {
  Container,
  StyledTable,
  TableHeaderRow,
  Th,
  Tr,
  Td,
  StepSelect,
  MobileCardList,
  MobileCard,
  MobileCardProgramName,
  MobileCardDates,
  MobileCardStepSelect,
} from "./styles";


export interface IdeasAccordionProps {
  orderBy: string;
}

export const IdeasAccordion = ({
  orderBy,
}: IdeasAccordionProps): JSX.Element => {
  const navigate = useNavigate();
  const menuEvaluations: IMenuEvaluators = useSelector(
    (state: TReducers) => state.menuEvaluatorsState,
  );

  const labels = {
    toEvaluate: trans("Avaliar"),
    evaluated: trans("Ver avaliação"),
    deadline: trans("Prazo para avaliação"),
    noContent: trans("Você ainda não tem soluções atribuídas"),
    evaluationNotStartedYet: trans("Fora do período de avaliações"),
    programs: trans("Programas"),
    status: trans("Status"),
    startDate: trans("Início da Etapa"),
    closingDate: trans("Fim da Etapa"),
    progress: trans("Progresso"),
    step: trans("Etapa"),
    evaluationDeadline: trans("Prazo de avaliação"),
    viewSolutions: trans("Ver soluções"),
    notStarted: trans("Não iniciada"),
    inProgress: trans("Em progresso"),
    completed: trans("Finalizado"),
  };

  const formatDate = (date: string | Date): string => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("pt-BR");
  };

  const [selectedSteps, setSelectedSteps] = useState<Record<string, string>>(
    {},
  );

  const handleStepChange = (evaluationId: string, stepId: string) => {
    setSelectedSteps((prev) => ({
      ...prev,
      [evaluationId]: stepId,
    }));
  };

  const getSelectedStep = (evaluation: any) => {
    const selectedStepId = selectedSteps[evaluation.id];

    return (
      evaluation.evaluation.steps.find((step) => step.id === selectedStepId) ??
      getCurrentStep(evaluation.evaluation)
    );
  };

  const getProgramName = (evaluation: any): string => {
    return evaluation.config.title
  };  

  const getCurrentStep = (evaluation: IMenuEvaluation): IMenuEvaluatorsSteps =>
    evaluation.steps.find((s) => s.isCurrent) ??
    (() => {
      throw new Error("Current evaluation step not found");
    })();

  const mapEvaluationRows = <T,>(
    renderFn: (params: {
      evaluation: any;
      selectedStep: any;
      programName: string;
      challengeId: string;
    }) => T,
  ): T[] => {
    if (!menuEvaluations.rows || (menuEvaluations.rows as any[]).length === 0) {
      return [];
    }

    return menuEvaluations.rows.map((evaluation) => {
      const selectedStep = getSelectedStep(evaluation);
      const programName = getProgramName(evaluation);
      const challengeId = evaluation.id;
      return renderFn({ evaluation, selectedStep, programName, challengeId });
    });
  };

  const renderCards = (): JSX.Element[] =>
    mapEvaluationRows(({ evaluation, selectedStep, programName, challengeId }) => (
        <MobileCard key={`card-${evaluation.id}`}>
          <MobileCardProgramName>{evaluation.config.title}</MobileCardProgramName>

          <MobileCardStepSelect
            value={selectedSteps[evaluation.id] ?? selectedStep?.id}
            onChange={(e) => handleStepChange(evaluation.id, e.target.value)}
          >
            {evaluation.evaluation.steps.map((step) => (
              <option key={step.id} value={step.id}>
                {step.name}
              </option>
            ))}
          </MobileCardStepSelect>

          <MobileCardDates>
            <span>{selectedStep ? formatDate(selectedStep.startDate) : "-"}</span>
            <span>→</span>
            <span>{selectedStep ? formatDate(selectedStep.closingDate) : "-"}</span>
          </MobileCardDates>

          <BaseButton
            variant="primary"
            size="medium"
            btnContent={labels.viewSolutions}
            onClick={() => {
              if (!selectedStep) return;
              navigate(
                `/hub/evaluator/challenge/${challengeId}/steps/${selectedStep.id}/solutions`,
                {
                  state: {
                    stepName: selectedStep.name,
                    programName: programName,
                    evaluationPeriod: `${formatDate(selectedStep.startDate)} - ${formatDate(selectedStep.closingDate)}`,
                  },
                },
              );
            }}
          />
        </MobileCard>
    ));

  const renderTableRows = (): JSX.Element[] =>
    mapEvaluationRows(({ evaluation, selectedStep, programName, challengeId }) => (
        <Tr key={evaluation.id}>
          <Td>{evaluation.config.title}</Td>

          <Td>
            <StepSelect
              value={selectedSteps[evaluation.id] ?? selectedStep?.id}
              onChange={(e) => handleStepChange(evaluation.id, e.target.value)}
            >
              {evaluation.evaluation.steps.map((step) => (
                <option key={step.id} value={step.id}>
                  {step.name}
                </option>
              ))}
            </StepSelect>
          </Td>

          <Td>{selectedStep ? formatDate(selectedStep.startDate) : "-"}</Td>

          <Td>{selectedStep ? formatDate(selectedStep.closingDate) : "-"}</Td>

          <Td center>
            <BaseButton
              variant="primary"
              size="medium"
              btnContent={labels.viewSolutions}
              onClick={() => {
                if (!selectedStep) return;

                navigate(
                  `/hub/evaluator/challenge/${challengeId}/steps/${selectedStep.id}/solutions`,
                  {
                    state: {
                      stepName: selectedStep.name,
                      programName: programName, 
                      evaluationPeriod: `${formatDate(selectedStep.startDate)} - ${ formatDate(selectedStep.closingDate)}`,                    },
                  },
                );
              }}
            />
          </Td>
        </Tr>
    ));


  return (
    <>
      {menuEvaluations.rows && menuEvaluations.rows.length > 0 ? (
        <Container>
          <StyledTable>
            <thead>
              <TableHeaderRow>
                <Th>{labels.programs}</Th>
                <Th>{labels.step}</Th>
                <Th width={150}>{labels.startDate}</Th>
                <Th width={150}>{labels.closingDate}</Th>
                <Th width={200} />
              </TableHeaderRow>
            </thead>

            <tbody>{renderTableRows()}</tbody>
          </StyledTable>

          <MobileCardList>{renderCards()}</MobileCardList>
        </Container>
      ) : (
        <NoContent
          text={labels.noContent}
          image={NoContentImg}
          imageWidth="350"
          imageHeight="182"
        />
      )}
    </>
  );
};
